import React, { useEffect, useRef, useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from "yup";
import InputField from '../../components/inputFields/InputField';
import { toast } from 'react-toastify';
import Button from '../../components/buttons/Button';
import Pagination from '../../components/tables/pagination/Pagination';
import Table from '../../components/tables/Table';
import { FiCheck } from 'react-icons/fi';
import ConfirmModal from '../../components/popups/confirmationModal/ConfirmModal';
import { addDose, completeDose, getDoses, getLastMachineDoses } from '../../services/clinic/doses';
import CustomLoading from '../../components/customLoading/CustomLoading';
import loaderImage from "../../assets/icons/part-logo.png";
import FormikDropdown from '../../components/inputFields/FormikDropDown';
import { formatDateTime } from '../../utils/FormatDateTime';
import { sendInvoiceVericifationOtp } from '../../services/clinic/invoice';
import AsyncFormikDropdown from '../../components/inputFields/AsyncFormikDropDown';
import { searchMasterPatientApi } from '../../services/clinic/patient_search';

function MachineDetails() {
    const location = useLocation();
    const [rowData, setRowData] = useState({});
    const navigate = useNavigate();
    const [tableData, setTableData] = useState({});

    const [userOptions, setUserOptions] = useState([]);
    const [otpText, setOtpText] = useState("");
    const [responseOtp, setResponseOtp] = useState(null);

    const headers = [
        { label: "Name", key: "full_name" },
        { label: "Energy", key: "user" },
        { label: "Tube", key: "serial_no" },
        { label: "Status", key: "device_type" },
        { label: "Date", key: "created_at" },
        { label: "Complete", key: "complete" },
    ];

    const [loading, setLoading] = useState(false);
    const [modalIsOpenEdit, setIsOpenEdit] = useState(false);
    const [editId, setEditId] = useState("");
    const [isConfirmEdit, setIsConfirmEdit] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [machineId, setMachineId] = useState("");

    const pageSize = 10;
    const timerRef = useRef(null);


    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const openEditModal = (row) => {
        setIsOpenEdit(true);
        setEditId(row?.id);
        setIsConfirmEdit(false);
    };

    const closeEditModal = () => {
        setIsOpenEdit(false);
        setEditId("");
        setIsConfirmEdit(false);
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (rowData.machine_id && tableData.results) {
            doseTimerStarter(rowData.machine_id, tableData.results);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [rowData.machine_id, tableData.results]);

    useEffect(() => {
        if (isConfirmEdit) {
            const fetchData = async () => {
                try {
                    const respones = await completeDose(editId);
                    if (respones?.status === 200) {
                        setIsOpenEdit(false);
                        fetchMachines(rowData?.machine_id);
                        toast.success("Dose completed successfully!");
                    } else {
                        let errors = "Opps! Something went wrong, please try again.";
                        toast.error(errors);
                    }
                } catch (error) {
                    let errors =
                        error?.response?.data?.detail ||
                        "Opps! Something went wrong, please try again.";
                    toast.error(errors);
                }
            };
            fetchData();
        }
    }, [isConfirmEdit]);

    const fetchMachines = async (machine_id) => {
        setLoading(true);
        try {
            let res = await getDoses({ machineId: machine_id });
            if (res.status === 200) {
                setTableData(res.data);
                doseTimerStarter(machine_id, res.data.results);
            } else {
                toast.error("Unable to fetch doses");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching Machines:", error);
        } finally {
            setLoading(false);
        }
    };

    const doseTimerStarter = async (machine_id, results) => {
        // Clear any existing timer before starting a new one
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        // Start the interval only if there are results and status isn't "Completed"
        if (results.length !== 0 && results[0].status !== "Completed") {
            timerRef.current = setInterval(() => {
                checkDose(machine_id, results[0]);
            }, 10000);
        }
    };

    const checkDose = async (machine_id, doseModelToCompareWith) => {
        try {
            let res = await getLastMachineDoses({ machineId: machine_id });
            if (res.status === 200) {
                const response = res.data;
                if (response.status === null || response.status !== doseModelToCompareWith.status) {
                    fetchMachines(machine_id);
                }
            } else {
                toast.error("Unable to check last dose");
            }
        } catch (error) {
            console.error("Error Checking Last Dose:", error);
        } finally {
        }
    };

    useEffect(() => {
        if (location.state) {
            setRowData(location.state);
            fetchMachines(location.state.machine_id);
        } else {
            navigate("/package-invoice");
            toast.error("Not able to open machine directly!");
        }
    }, [location]);

    const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const formData = new FormData();
            formData.append("full_name", values.name);
            formData.append("tube", values.tube);
            formData.append("j_cm2", values.energy);
            formData.append("machine_id", rowData.machine_id);

            if (otpText.length === 0) {
                toast.error("Provide OTP");
                return;
            } else if (otpText !== responseOtp) {
                toast.error("Wrong OTP Provided");
                return;
            }

            const response = await addDose(formData);

            if (response?.status === 201) {
                fetchMachines(rowData?.machine_id);
                toast.success("Dose added successfully!");
                resetForm();
                setOtpText("");
                setResponseOtp(null);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };

    const handleOtpFill = (value) => {
        if (value) {
            if (otpText.length !== 0) {
                if (otpText.length < 4) {
                    setOtpText(otpText + value);
                }
            } else {
                setOtpText(value);
            }
        } else {
            let updatedText = otpText.slice(0, -1);
            setOtpText(updatedText);
        }
    };

    const handleOtpSend = async (values) => {
        try {
            const formData = new FormData();
            formData.append("mobile_number", values.alt_phone);
            const response = await sendInvoiceVericifationOtp(formData);

            if (response?.status === 200) {
                toast.success("OTP Sent successfully!");
                setResponseOtp(response.data.otp);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
    };

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/invoice' },
            { name: 'Machines' },
            { name: rowData.machine_name },
        ]} />
        <div className="py-3">
            <Formik
                initialValues={{
                    name: "",
                    energy: "",
                    phone: "",
                    alt_phone: "",
                    tube: "",
                    otp: "",
                }}
                validationSchema={Yup.object({
                    name: Yup.string().required("Please provide Name"),
                    energy: Yup.string().required("Please provide Energy"),
                    tube: Yup.string().required("Please Select Tube"),
                    alt_phone: Yup.string().required("Alt. Phone is required"),
                })}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, setFieldValue, resetForm, values }) => (
                    <Form>
                        <div className="grid gap-2 grid-cols-1 lg:grid-cols-4 border border-gray-300 p-4 rounded-lg bg-gray-100">
                            <AsyncFormikDropdown
                                label="Patient ID"
                                name="patient"
                                placeholder="Select Patient"
                                apiCallFunction={async (input) => {
                                    const response = await searchMasterPatientApi(input);
                                    setUserOptions(response.data);
                                    return response.data.map((patient) => ({
                                        value: patient.id,
                                        label: `${patient.patient_name} | ${patient.patient_phone}`,
                                    }));
                                }}
                                required
                                needSearch={true}
                                onChange={(selectedOption) => {
                                    // Find the corresponding patient data                                            
                                    const selectedPatient = userOptions.find(
                                        (item) => item.id === selectedOption.value
                                    );

                                    if (selectedPatient) {
                                        setFieldValue("name", selectedPatient.patient_name);
                                        setFieldValue("phone", selectedPatient.patient_phone);
                                        setFieldValue("alt_phone", selectedPatient.patient_phone);
                                    }
                                }}
                            />
                            <InputField
                                label="Name"
                                name="name"
                                type="text"
                                placeholder="Enter Name"
                                required
                                disable
                            />
                            <InputField
                                label="Phone"
                                name="phone"
                                type="text"
                                placeholder="Enter Phone Number"
                                required
                                className="hidden"
                                disable
                            />
                            <InputField
                                label="Alt. Phone"
                                name="alt_phone"
                                type="text"
                                placeholder="Enter Phone Number"
                                className={responseOtp ? "" : "hidden"}
                                required
                            />
                            <InputField
                                label="Energy (J/cm2)"
                                name="energy"
                                placeholder="Energy"
                                required
                            />
                            <FormikDropdown
                                label="Tube"
                                name="tube"
                                placeholder='Select Tube'
                                options={[
                                    { value: "Tube A", label: "Tube A" },
                                    { value: "Tube B", label: "Tube B" },
                                ]}
                                required
                            />
                            <div className="flex flex-col justify-end pb-2">
                                <Button
                                    type="button"
                                    className="text-primary bg-white hover:bg-white-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center border border-primary"
                                    onClick={() => handleOtpSend(values)}>
                                    Generate OTP
                                </Button>
                            </div>
                            <InputField
                                label="OTP"
                                name="otp"
                                placeholder={"Enter OTP"}
                                type="number"
                                value={otpText}
                                onChange={(val) => handleOtpFill(val.nativeEvent.data)}
                                required
                            />
                            <div className="flex flex-col justify-end pb-2">
                                <Button
                                    type="submit"
                                    className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center"
                                    disabled={isSubmitting || responseOtp === null}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
        {
            loading ?
                <div className="flex w-full flex-row justify-center my-2">
                    <CustomLoading imageSrc={loaderImage} size={100} />
                </div>
                :
                <div className="mt-2">
                    <Table
                        headers={headers}
                        rows={tableData?.results?.map((item, index) => (
                            {
                                full_name: <p>{item.full_name}</p>,
                                user: <p>{item.j_cm2}</p>,
                                serial_no: <p>{item.tube}</p>,
                                device_type: <p>{item.status}</p>,
                                created_at: <p>{formatDateTime(item.created_at)}</p>,
                                complete: item.status === "Not Started" ? (
                                    <FiCheck
                                        size={23}
                                        onClick={() =>
                                            openEditModal({
                                                id: item?.machine_hardware_id,
                                                model: item,
                                            })
                                        }
                                        className="cursor-pointer text-primary"
                                    />
                                ) : null,
                            }))}
                    />
                </div>}
        {/* Table End
        {/* Pagination Begin */}
        {
            tableData.count !== 0 ?
                <div className="xl:pl-5 pl-0">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={Math.ceil(tableData?.count / pageSize)}
                        pageSize={pageSize}
                        totalEntries={tableData?.count}
                        onPageChange={handlePageChange}
                        data={tableData}
                        setTableData={setTableData}
                    />
                </div> : <></>
        }

        <ConfirmModal
            isOpen={modalIsOpenEdit}
            onRequestClose={closeEditModal}
            contentLabel=""
            onClose={closeEditModal}
            setIsConfirmDelete={setIsConfirmEdit}
            title="Are you sure, you want to complete this dose?"
            icon={<FiCheck
                size={23}
                className="cursor-pointer text-primary"
            />}
        />
    </>);
}

export default MachineDetails;